import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, FileText, Play } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Section } from "@/components/Section";

export default function Media() {
  const [tab, setTab] = useState("articles");
  const [blogs, setBlogs] = useState([]);
  const [podcasts, setPodcasts] = useState([]);

  useEffect(() => {
    api.get("/blogs").then((r) => setBlogs(r.data)).catch(() => {});
    api.get("/podcasts").then((r) => setPodcasts(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <section className="bg-[#1A1A1A] text-white py-20 noise-overlay">
        <Section>
          <div className="label-eyebrow mb-4">Media</div>
          <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl">
            INDUSTRY <span className="text-[#E65C00]">READING & LISTENING.</span>
          </h1>
          <p className="mt-4 text-gray-400 max-w-xl">Articles, podcasts and field videos from the people who sell the steel.</p>
        </Section>
      </section>

      <Section className="py-12">
        <div className="flex gap-0 border-b-2 border-gray-200 mb-8" data-testid="media-tabs">
          <button
            onClick={() => setTab("articles")}
            className={`flex items-center gap-2 px-6 py-4 font-heading font-black uppercase tracking-widest text-sm -mb-0.5 border-b-4 transition-colors ${tab === "articles" ? "border-[#E65C00] text-[#1A1A1A]" : "border-transparent text-gray-400 hover:text-[#1A1A1A]"}`}
            data-testid="media-tab-articles"
          >
            <FileText className="w-4 h-4" /> Articles ({blogs.length})
          </button>
          <button
            onClick={() => setTab("podcasts")}
            className={`flex items-center gap-2 px-6 py-4 font-heading font-black uppercase tracking-widest text-sm -mb-0.5 border-b-4 transition-colors ${tab === "podcasts" ? "border-[#E65C00] text-[#1A1A1A]" : "border-transparent text-gray-400 hover:text-[#1A1A1A]"}`}
            data-testid="media-tab-podcasts"
          >
            <Headphones className="w-4 h-4" /> Podcasts ({podcasts.length})
          </button>
        </div>

        {tab === "articles" ? (
          blogs.length === 0
            ? <div className="text-center py-20 border border-dashed border-gray-300 text-gray-500">No articles yet.</div>
            : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogs.map((b) => (
                  <Link key={b.id} to={`/blog/${b.slug}`} className="card-industrial block group" data-testid={`media-blog-${b.id}`}>
                    <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                      <img src={mediaUrl(b.cover_image)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <div className="label-eyebrow !text-gray-500">{b.category}</div>
                      <h3 className="font-heading font-black text-lg mt-2 leading-tight group-hover:text-[#E65C00]">{b.title}</h3>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-3">{b.excerpt}</p>
                      <div className="text-xs text-gray-400 mt-4 uppercase tracking-widest">{b.author}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )
        ) : (
          podcasts.length === 0
            ? <div className="text-center py-20 border border-dashed border-gray-300 text-gray-500">No podcast episodes yet.</div>
            : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {podcasts.map((p) => (
                  <Link key={p.id} to={`/podcasts`} className="card-industrial block group relative" data-testid={`media-podcast-${p.id}`}>
                    <div className="aspect-square overflow-hidden bg-gray-100 relative">
                      <img src={mediaUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
                      <div className="absolute bottom-3 left-3 bg-[#E65C00] text-white w-10 h-10 flex items-center justify-center">
                        <Play className="w-5 h-5 ml-0.5" />
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-[#E65C00] font-bold">EP {p.episode_number} · {p.duration || "—"}</span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 ${(p.media_type || "audio") === "audio" ? "bg-[#FFCC00] text-[#1A1A1A]" : "bg-[#1A1A1A] text-white"}`}>
                          {(p.media_type || "audio") === "audio" ? "AUDIO" : "VIDEO"}
                        </span>
                      </div>
                      <h3 className="font-heading font-black mt-2 leading-tight group-hover:text-[#E65C00]">{p.title}</h3>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{p.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )
        )}
      </Section>
    </>
  );
}
